import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person as UserIcon,
  CreditCard as CreditCardIcon,
  AttachMoney as DollarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Warning as AlertTriangleIcon,
  Receipt as ReceiptIcon,
  Calculate as CalculatorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import api from '../../services/api';

interface ValidacionDocumentoRequest {
  tipo_documento: 'dni' | 'carnet_extranjeria' | 'pasaporte';
  numero_documento: string;
  comercio_id: number;
  monto_consumo: number;
}

interface ConvenioAplicable {
  id: number;
  nombre: string;
  descripcion: string;
  porcentaje_descuento?: number;
  monto_descuento?: number;
  descuento_calculado: number;
  descuento_aplicable: number;
  monto_maximo_descuento: number;
  cumple_consumo_minimo: boolean;
  usos_disponibles: number | 'ilimitado';
  restricciones: string[];
}

interface ValidacionDocumentoResponse {
  elegible: boolean;
  usuario?: {
    id: number;
    nombres: string;
    apellidos: string;
    email: string;
    tipo_documento: string;
    numero_documento: string;
  };
  convenios_aplicables: ConvenioAplicable[];
  mensaje?: string;
  error?: string;
}

interface RedencionRequest {
  tipo_documento: string;
  numero_documento: string;
  convenio_id: number;
  monto_consumo: number;
  descuento_aplicado: number;
  observaciones?: string;
}

const VerificacionDocumento: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);
  
  // Estado del formulario
  const [tipoDocumento, setTipoDocumento] = useState<'dni' | 'carnet_extranjeria' | 'pasaporte'>('dni');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [montoConsumo, setMontoConsumo] = useState('');
  
  // Estado de la validación
  const [validacion, setValidacion] = useState<ValidacionDocumentoResponse | null>(null);
  const [convenioSeleccionado, setConvenioSeleccionado] = useState<number | null>(null);
  const [observaciones, setObservaciones] = useState('');
  
  // Estado de mensajes
  const [mensaje, setMensaje] = useState<{ tipo: 'success' | 'error' | 'warning'; texto: string } | null>(null);
  
  // Estado del modal de éxito
  const [modalExito, setModalExito] = useState<{
    abierto: boolean;
    descuento: number;
    documento: string;
    convenio: string;
  }>({
    abierto: false,
    descuento: 0,
    documento: '',
    convenio: ''
  });

  const limpiarFormulario = () => {
    setNumeroDocumento('');
    setMontoConsumo('');
    setValidacion(null);
    setConvenioSeleccionado(null);
    setObservaciones('');
    setMensaje(null);
  };

  const validarFormato = (tipo: string, numero: string): boolean => {
    switch (tipo) {
      case 'dni':
        return /^\d{8}$/.test(numero);
      case 'carnet_extranjeria':
        return /^[A-Z]{2}\d{9}$/.test(numero.toUpperCase());
      case 'pasaporte':
        return /^[A-Z]{2}\d{7}$/.test(numero.toUpperCase());
      default:
        return false;
    }
  };

  const handleValidarDocumento = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!numeroDocumento || !montoConsumo) {
      setMensaje({ tipo: 'error', texto: 'Por favor complete todos los campos requeridos' });
      return;
    }

    if (!validarFormato(tipoDocumento, numeroDocumento)) {
      setMensaje({ tipo: 'error', texto: 'El formato del documento no es válido' });
      return;
    }

    const monto = parseFloat(montoConsumo);
    if (isNaN(monto) || monto <= 0) {
      setMensaje({ tipo: 'error', texto: 'El monto de consumo debe ser mayor a 0' });
      return;
    }

    setLoading(true);
    setMensaje(null);

    try {
      const request: ValidacionDocumentoRequest = {
        tipo_documento: tipoDocumento,
        numero_documento: numeroDocumento.toUpperCase(),
        comercio_id: user?.id || 0,
        monto_consumo: monto
      };

      const response = await api.validarDocumento(request);
      
      if (response.success && response.data.elegible) {
        setValidacion(response.data);
        setMensaje({ tipo: 'success', texto: response.data.mensaje || 'Documento válido para descuentos' });
      } else {
        setValidacion(response.data);
        setMensaje({ tipo: 'warning', texto: response.data.mensaje || 'Documento no elegible para descuentos' });
      }
    } catch (error: any) {
      console.error('Error validando documento:', error);
      setMensaje({ 
        tipo: 'error', 
        texto: error.response?.data?.message || 'Error validando el documento' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcesarRedencion = async () => {
    if (!validacion || !convenioSeleccionado) {
      setMensaje({ tipo: 'error', texto: 'Debe seleccionar un convenio para procesar' });
      return;
    }

    const convenio = validacion.convenios_aplicables.find(c => c.id === convenioSeleccionado);
    if (!convenio) {
      setMensaje({ tipo: 'error', texto: 'Convenio no encontrado' });
      return;
    }

    setProcesando(true);
    setMensaje(null);

    try {
      const request: RedencionRequest = {
        tipo_documento: tipoDocumento,
        numero_documento: numeroDocumento.toUpperCase(),
        convenio_id: convenioSeleccionado,
        monto_consumo: parseFloat(montoConsumo),
        descuento_aplicado: convenio.descuento_aplicable,
        observaciones: observaciones || 'Redención por verificación de documento'
      };

      const response = await api.redimirDocumento(request);
      
      if (response.success) {
        // Mostrar modal de éxito en lugar de mensaje que se refresca
        setModalExito({
          abierto: true,
          descuento: convenio.descuento_aplicable,
          documento: `${formatearTipoDocumento(tipoDocumento)}: ${numeroDocumento.toUpperCase()}`,
          convenio: convenio.nombre
        });
      }

    } catch (error: any) {
      console.error('Error procesando redención:', error);
      setMensaje({ 
        tipo: 'error', 
        texto: error.response?.data?.message || 'Error procesando la redención' 
      });
    } finally {
      setProcesando(false);
    }
  };

  const handleCerrarModal = () => {
    setModalExito({
      abierto: false,
      descuento: 0,
      documento: '',
      convenio: ''
    });
    // Limpiar formulario después de cerrar el modal
    limpiarFormulario();
  };

  const formatearTipoDocumento = (tipo: string): string => {
    switch (tipo) {
      case 'dni': return 'DNI';
      case 'carnet_extranjeria': return 'Carnet de Extranjería';
      case 'pasaporte': return 'Pasaporte';
      default: return tipo;
    }
  };

  const formatearUsosDisponibles = (usos: number | 'ilimitado'): string => {
    return usos === 'ilimitado' ? 'Ilimitado' : `${usos} uso(s) disponible(s)`;
  };

  const getPlaceholder = (): string => {
    switch (tipoDocumento) {
      case 'dni': return '12345678';
      case 'carnet_extranjeria': return 'CE123456789';
      case 'pasaporte': return 'PA1234567';
      default: return '';
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Card>
          <CardHeader>
            <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UserIcon />
              Verificación por Documento de Identidad
            </Typography>
          </CardHeader>
          <CardContent>
            {/* Formulario de verificación */}
            <Box component="form" onSubmit={handleValidarDocumento} sx={{ mb: 4 }}>
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Documento</InputLabel>
                      <Select
                        value={tipoDocumento}
                        label="Tipo de Documento"
                        onChange={(e) => setTipoDocumento(e.target.value as 'dni' | 'carnet_extranjeria' | 'pasaporte')}
                      >
                        <MenuItem value="dni">DNI</MenuItem>
                        <MenuItem value="carnet_extranjeria">Carnet de Extranjería</MenuItem>
                        <MenuItem value="pasaporte">Pasaporte</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Número de Documento"
                      value={numeroDocumento}
                      onChange={(e) => setNumeroDocumento(e.target.value.toUpperCase())}
                      placeholder={getPlaceholder()}
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                    />
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Monto de Consumo"
                  type="number"
                  value={montoConsumo}
                  onChange={(e) => setMontoConsumo(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DollarIcon />
                        S/
                      </InputAdornment>
                    ),
                  }}
                />

                <Button 
                  type="submit" 
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Validando...' : 'Validar Documento'}
                </Button>
              </Stack>
            </Box>

            {/* Mensajes */}
            {mensaje && (
              <Alert 
                severity={mensaje.tipo} 
                sx={{ mb: 3 }}
                icon={
                  mensaje.tipo === 'error' ? <XCircleIcon /> :
                  mensaje.tipo === 'warning' ? <AlertTriangleIcon /> :
                  <CheckCircleIcon />
                }
              >
                {mensaje.texto}
              </Alert>
            )}

            {/* Resultados de validación */}
            {validacion && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                
                {/* Información del usuario */}
                {validacion.usuario && (
                  <Card sx={{ mb: 3 }}>
                    <CardHeader>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UserIcon />
                        Información del Usuario
                      </Typography>
                    </CardHeader>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Nombres y Apellidos
                            </Typography>
                            <Typography variant="h6">
                              {validacion.usuario.nombres} {validacion.usuario.apellidos}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Email
                            </Typography>
                            <Typography variant="body1">
                              {validacion.usuario.email}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Tipo de Documento
                            </Typography>
                            <Typography variant="body1">
                              {formatearTipoDocumento(validacion.usuario.tipo_documento)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Número de Documento
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                              {validacion.usuario.numero_documento}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Convenios aplicables */}
                {validacion.convenios_aplicables.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardHeader>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon />
                        Convenios Disponibles
                      </Typography>
                    </CardHeader>
                    <CardContent>
                      <Stack spacing={2}>
                        {validacion.convenios_aplicables.map((convenio) => (
                          <Paper 
                            key={convenio.id}
                            sx={{ 
                              p: 2, 
                              cursor: 'pointer',
                              border: convenioSeleccionado === convenio.id ? 2 : 1,
                              borderColor: convenioSeleccionado === convenio.id ? 'primary.main' : 'grey.300',
                              backgroundColor: convenioSeleccionado === convenio.id ? 'primary.50' : 'background.paper',
                              '&:hover': { borderColor: 'primary.main' }
                            }}
                            onClick={() => setConvenioSeleccionado(convenio.id)}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                  {convenio.nombre}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  {convenio.descripcion}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 4, mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                                      Descuento Calculado
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                                        S/ {convenio.descuento_aplicable.toFixed(2)}
                                      </Typography>
                                      {convenio.porcentaje_descuento && (
                                        <Chip 
                                          label={`${convenio.porcentaje_descuento}%`} 
                                          size="small" 
                                          color="secondary"
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', mt: 1 }}>
                                      Monto máximo de descuento: <span style={{ color: '#1976d2', fontWeight: 700 }}>S/ {(typeof convenio.monto_maximo_descuento === 'number' ? convenio.monto_maximo_descuento : parseFloat(convenio.monto_maximo_descuento || '0')).toFixed(2)}</span>
                                    </Typography>
                                  </Box>
                                  
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                                      Usos Disponibles
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                      {formatearUsosDisponibles(convenio.usos_disponibles)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              
                              <Box sx={{ ml: 2 }}>
                                {convenioSeleccionado === convenio.id && (
                                  <CheckCircleIcon color="primary" />
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Observaciones y procesamiento */}
                {validacion.convenios_aplicables.length > 0 && (
                  <Card>
                    <CardHeader>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon />
                        Procesar Descuento
                      </Typography>
                    </CardHeader>
                    <CardContent>
                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Observaciones (opcional)"
                          value={observaciones}
                          onChange={(e) => setObservaciones(e.target.value)}
                          placeholder="Ingrese observaciones adicionales..."
                          multiline
                          rows={2}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button 
                            variant="contained"
                            onClick={handleProcesarRedencion}
                            disabled={!convenioSeleccionado || procesando}
                            startIcon={procesando ? <CircularProgress size={20} /> : <CalculatorIcon />}
                            sx={{ flex: 1 }}
                          >
                            {procesando ? 'Procesando...' : 'Aplicar Descuento'}
                          </Button>
                          
                          <Button 
                            variant="outlined" 
                            onClick={limpiarFormulario}
                            disabled={procesando}
                            startIcon={<RefreshIcon />}
                          >
                            Nueva Consulta
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Modal de éxito */}
        <Dialog 
          open={modalExito.abierto} 
          onClose={handleCerrarModal}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h4" component="div">
              ¡Descuento Aplicado!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h2" color="success.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                S/ {modalExito.descuento.toFixed(2)}
              </Typography>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Documento
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {modalExito.documento}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Convenio Aplicado
                  </Typography>
                  <Typography variant="h6">
                    {modalExito.convenio}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Monto de Consumo
                  </Typography>
                  <Typography variant="h6">
                    S/ {montoConsumo}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="success" sx={{ mt: 3 }}>
                La redención ha sido registrada exitosamente. Puede revisar todas las redenciones en la sección "Redenciones" del menú.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={handleCerrarModal}
              variant="contained" 
              size="large"
              startIcon={<RefreshIcon />}
            >
              Nueva Consulta
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default VerificacionDocumento;
